import Take3D from "../models/Take3D";
import { TakeAttachment } from "../models/TakeAttachment";
import Interaction, { InteractionType } from "../models/interactions/Interaction";
import { IAttach } from "../models/interactions/Attach";
import { IDetach } from "../models/interactions/Detach";
import { IGrab } from "../models/interactions/Grab";
import { ISequence } from "../models/interactions/SequenceModel";
// Type-only: loading the Trigger model pulls in its node preview components.
import type { ITrigger, TriggerType } from "../models/interactions/Trigger";
import {
  getSequenceTerminalEffects,
  getTriggerTerminalEffects,
  InteractionEffect,
  NO_EFFECT,
} from "./SequenceGraphEffects";

export interface TakeEndState {
  /** Attachments every path through the take ends with. */
  attachments: Record<string, TakeAttachment>;
  /**
   * Props that end attached on some paths and not on others (or to different
   * actors). They keep the take's starting attachment.
   */
  ambiguousPropIds: string[];
}

const DETACHED = "detached";
// The compiler always attaches a grabbed prop to this tag.
const GRAB_TAG = "Take";
const ON_TAKE_START = "onStart" as TriggerType;
const ON_TAKE_END = "onEnd" as TriggerType;

// A path's state for one prop: DETACHED, or the attachment it ends with.
const encodeAttachment = (attachment: TakeAttachment) =>
  JSON.stringify([
    attachment.id,
    attachment.tag ?? "",
    attachment.playerToProp ?? false,
  ]);

const decodeAttachment = (state: string): TakeAttachment => {
  const [id, tag, playerToProp] = JSON.parse(state);
  return { id, tag, playerToProp };
};

const getModifiedPropId = (interaction: Interaction): string | undefined => {
  switch (interaction.type) {
    case InteractionType.Attach:
    case InteractionType.Grab:
    case InteractionType.Detach:
      return (interaction as IAttach | IGrab | IDetach).propGuid || undefined;
    default:
      return undefined;
  }
};

const getAttachmentEffect =
  (propId: string): InteractionEffect =>
  (interaction) => {
    if (getModifiedPropId(interaction) !== propId) return NO_EFFECT;

    if (interaction.type === InteractionType.Detach) return DETACHED;

    const attach = interaction as IAttach | IGrab;
    if (!attach.playerGuid) return NO_EFFECT;
    return encodeAttachment({
      id: attach.playerGuid,
      tag:
        interaction.type === InteractionType.Grab
          ? attach.tag || GRAB_TAG
          : attach.tag ?? "",
      playerToProp:
        interaction.type === InteractionType.Attach
          ? (attach as IAttach).playerToProp ?? false
          : false,
    });
  };

const applyEffects = (states: Set<string>, effects: Set<string>) => {
  // No path finishes (e.g. a loop): nothing reliable to apply.
  if (effects.size === 0) return states;

  const next = new Set<string>();
  effects.forEach((effect) => {
    if (effect === NO_EFFECT) states.forEach((state) => next.add(state));
    else next.add(effect);
  });
  return next;
};

const collectSequencePropIds = (sequence: ISequence | undefined, into: Set<string>) =>
  (sequence?.nodes ?? []).forEach((node) => {
    const propId = getModifiedPropId(node.data.interaction);
    if (propId) into.add(propId);
  });

const findTriggerNodeId = (sequence: ISequence, triggerType: TriggerType) =>
  sequence.nodes?.find(
    (node) =>
      node.data.interaction.type === InteractionType.Trigger &&
      (node.data.interaction as ITrigger).triggerType === triggerType,
  )?.id;

/**
 * How a take leaves its attachments, in the order the compiled take runs:
 * its starting attachments, On Take Start, the interaction list in order, then
 * On Take End. The next take starts from this.
 */
export function getTakeEndState(take: Take3D): TakeEndState {
  const propIds = new Set(Object.keys(take.attachments ?? {}));
  (take.interactions ?? []).forEach((interaction) => {
    const propId = getModifiedPropId(interaction);
    if (propId) propIds.add(propId);
    if (interaction.type === InteractionType.Sequence)
      collectSequencePropIds(interaction as ISequence, propIds);
  });
  collectSequencePropIds(take.sequence, propIds);

  const attachments: Record<string, TakeAttachment> = {};
  const ambiguousPropIds: string[] = [];

  propIds.forEach((propId) => {
    const start = take.attachments?.[propId];
    let states = new Set([start ? encodeAttachment(start) : DETACHED]);
    const effectOf = getAttachmentEffect(propId);

    const applyTrigger = (triggerType: TriggerType) => {
      const triggerId = take.sequence && findTriggerNodeId(take.sequence, triggerType);
      if (!triggerId) return;
      states = applyEffects(
        states,
        getTriggerTerminalEffects(take.sequence, triggerId, effectOf),
      );
    };

    applyTrigger(ON_TAKE_START);
    (take.interactions ?? []).forEach((interaction) => {
      const effects =
        interaction.type === InteractionType.Sequence
          ? getSequenceTerminalEffects(interaction as ISequence, effectOf)
          : new Set([effectOf(interaction)]);
      states = applyEffects(states, effects);
    });
    applyTrigger(ON_TAKE_END);

    // Can't tell how the take leaves it: keep how the take started with it.
    if (states.size > 1) {
      ambiguousPropIds.push(propId);
      if (start) attachments[propId] = { ...start };
      return;
    }

    const [state] = [...states];
    if (state !== DETACHED) attachments[propId] = decodeAttachment(state);
  });

  return { attachments, ambiguousPropIds };
}

/** A Prop-to-Player attachment holds the prop instance, so it can't also be placed. */
export const isHeldByAttachment = (
  attachments: Record<string, TakeAttachment>,
  propId: string,
) => {
  const attachment = attachments[propId];
  return Boolean(attachment) && !attachment.playerToProp;
};

export interface TakeStartMismatch {
  propId: string;
  /** How the previous take leaves the prop; undefined when it ends detached. */
  expected?: TakeAttachment;
  actual?: TakeAttachment;
}

const sameAttachment = (first?: TakeAttachment, second?: TakeAttachment) =>
  first === undefined || second === undefined
    ? first === second
    : first.id === second.id &&
      (first.tag ?? "") === (second.tag ?? "") &&
      (first.playerToProp ?? false) === (second.playerToProp ?? false);

/**
 * Props the previous take attaches or detaches during the take, where this
 * take doesn't start the way the previous one ends. A take created before that
 * change still carries the old state.
 */
export function getTakeStartMismatches(
  previousTake: Take3D | undefined,
  take: Take3D,
): TakeStartMismatch[] {
  if (!previousTake) return [];

  const { attachments: ended, ambiguousPropIds } = getTakeEndState(previousTake);
  const started = previousTake.attachments ?? {};
  const propIds = new Set([...Object.keys(ended), ...Object.keys(started)]);

  return [...propIds]
    .filter((propId) => !ambiguousPropIds.includes(propId))
    .filter((propId) => !sameAttachment(ended[propId], started[propId]))
    .filter((propId) => !sameAttachment(ended[propId], take.attachments?.[propId]))
    // An actor attached to a prop the take no longer places: the compiled take
    // despawns that prop, so there is nothing left to stay attached to.
    .filter((propId) => {
      const expected = ended[propId];
      return !expected?.playerToProp ||
        (take.props ?? []).some((prop) => prop.instanceId === propId);
    })
    // An actor that moves gets off a Player-to-Prop attachment before moving,
    // so this take starts without it (getMovedHardAttachments covers a take
    // that still lists it).
    .filter((propId) => {
      const expected = ended[propId];
      return !expected?.playerToProp || !actorMoves(previousTake, take, expected.id);
    })
    .map((propId) => ({
      propId,
      expected: ended[propId],
      actual: take.attachments?.[propId],
    }));
}

/**
 * Props an actor holds at the start of the take that are also still placed
 * (made before placing and attaching excluded each other). The compiled take
 * starts them in the hand.
 */
export const getPlacedHeldPropIds = (take: Take3D): string[] =>
  (take.props ?? [])
    .map((prop) => prop.instanceId)
    .filter((propId) => isHeldByAttachment(take.attachments ?? {}, propId));

/** Whether the actor stands on a different position in `take` than in `previousTake`. */
function actorMoves(previousTake: Take3D, take: Take3D, actorId: string) {
  const positionOf = (owner: Take3D) =>
    owner.actors?.find((actor) => actor.actorId === actorId)?.positionId;
  const from = positionOf(previousTake);
  const to = positionOf(take);
  return Boolean(from) && Boolean(to) && from !== to;
}

/**
 * Player-to-Prop attachments (an actor on a forklift seat) the take keeps while
 * moving that actor to another position. The prop decides where the actor is,
 * so the actor has to get off before moving: Fix take start drops the attachment.
 */
export function getMovedHardAttachments(
  previousTake: Take3D | undefined,
  take: Take3D,
): { propId: string; actorId: string }[] {
  if (!previousTake) return [];
  const { attachments: ended } = getTakeEndState(previousTake);

  return Object.entries(take.attachments ?? {})
    .filter(([propId, attachment]) =>
      attachment.playerToProp && sameAttachment(ended[propId], attachment))
    .filter(([, attachment]) => actorMoves(previousTake, take, attachment.id))
    .map(([propId, attachment]) => ({ propId, actorId: attachment.id }));
}

/**
 * Starts the take the way the previous take ends for the mismatched props,
 * drops the Player-to-Prop attachments of actors that move, and takes held
 * props off their positions.
 */
export function matchTakeStartToPreviousTakeEnd(
  previousTake: Take3D | undefined,
  take: Take3D,
): Take3D {
  const mismatches = getTakeStartMismatches(previousTake, take);
  const moved = getMovedHardAttachments(previousTake, take);
  if (mismatches.length === 0 && moved.length === 0 && getPlacedHeldPropIds(take).length === 0)
    return take;

  const attachments = { ...take.attachments };
  mismatches.forEach(({ propId, expected }) => {
    if (expected) attachments[propId] = { ...expected };
    else delete attachments[propId];
  });
  moved.forEach(({ propId }) => delete attachments[propId]);

  return {
    ...take,
    attachments,
    props: take.props.filter(
      (prop) => !isHeldByAttachment(attachments, prop.instanceId),
    ),
  };
}

export function describeTakeStartMismatch(
  mismatch: TakeStartMismatch,
  previousTakeName: string,
  getPropName: (propId: string) => string,
  getActorName: (actorId: string) => string,
): string {
  const prop = getPropName(mismatch.propId);
  if (mismatch.expected)
    return `${previousTakeName} ends with ${prop} attached to ${getActorName(mismatch.expected.id)}, but this take doesn't start that way.`;
  return `${previousTakeName} ends with ${prop} detached, but this take still starts with it attached to ${getActorName(mismatch.actual!.id)}.`;
}
